import { useEffect, useMemo, useState, type FormEvent } from "react";
import { TeamImport } from "../../components/TeamImport";
import { addDoc, arrayRemove, arrayUnion, collection, deleteDoc, doc, onSnapshot, orderBy, query, updateDoc } from "firebase/firestore";
import { ChevronDown, ChevronRight, FileUp, UserPlus } from "lucide-react";
import { formatHomeAddress, paths, ROLE_LABEL, ROLE_RANK, type Department, type HomeAddress, type Role } from "@clevis/shared-types";
import { Button, Card, CardTitle, ConfirmButton, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { TwoStepTeamLine } from "../../components/TwoStepTeamLine";
import { CommissionsPanel } from "../../components/CommissionsPanel";
import { useSalesSettings } from "../../hooks/useSalesSettings";
import { useAccess } from "../../hooks/useAccess";
import { useEntitlements } from "../../hooks/useEntitlements";
import { Avatar, BulkBar, ListFooter, SearchBox, SortHeader, useListView } from "../../components/ListControls";

// Team: People (search, sort, filter by role / department / status, bulk role
// and department changes, details per person), Waiting to join (invites:
// role per person, send selected), and Departments (manager + members).
// Lists show 25 at a time; a company with 50 people stays one screen.

export interface MemberRow {
  uid: string;
  displayName: string;
  email: string;
  role: Role;
  disabled: boolean;
  offShiftRadiusKm: number | null;
  profile: { approved?: boolean; backgroundChecked?: boolean; photoUrl?: string | null; bio?: string; publicName?: string } | null;
  /** From a team import (or payroll). */
  title?: string;
  phone?: string;
}
export interface InviteRow {
  token: string;
  email: string;
  role: Role;
  expiresAt: number;
  lastEmailedAt: number | null;
}
interface Sensitive {
  hourlyRateCents: number | null;
  commissionRateBps?: number | null;
  offShiftRadiusKm: number | null;
  homeAddress?: HomeAddress | null;
}
export type Dept = Department & { id: string };
type Tab = "people" | "invites" | "departments" | "commissions";

const ROLES: Role[] = ["owner", "admin", "manager", "staff", "tech"];
const shortDay = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const nameOf = (m: { displayName: string; email: string }) => m.displayName || m.email;
const tabFromHash = (): Tab | null => {
  const h = window.location.hash.replace("#", "");
  return h === "invites" ? "invites" : h === "departments" ? "departments" : h === "commissions" ? "commissions" : h === "people" ? "people" : null;
};

export function TeamPage() {
  const { tenantId, user, role } = useAuth();
  const can = useAccess();
  const isAdminRole = role === "owner" || role === "admin";
  const isAdmin = isAdminRole && can("team.invites");
  const sales = useSalesSettings(tenantId);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [departments, setDepartments] = useState<Dept[]>([]);
  const [sensitive, setSensitive] = useState<Record<string, Sensitive>>({});
  const [tab, setTab] = useState<Tab>(() => tabFromHash() ?? "people");
  const [message, setMessage] = useState("");
  const { hasFeature } = useEntitlements();
  const deptsOn = hasFeature("departments");

  useEffect(() => {
    const onHash = (e: Event) => {
      const h = (e as CustomEvent<string>).detail;
      if (h === "invites" || h === "departments" || h === "commissions" || h === "people") setTab(h);
    };
    const onNative = () => {
      const t = tabFromHash();
      if (t) setTab(t);
    };
    window.addEventListener("clevis:hash", onHash);
    window.addEventListener("hashchange", onNative);
    return () => {
      window.removeEventListener("clevis:hash", onHash);
      window.removeEventListener("hashchange", onNative);
    };
  }, []);

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(query(collection(db, paths.tenantMembers(tenantId)), orderBy("displayName")), (snap) =>
        setMembers(
          snap.docs.map((d) => ({
            uid: d.id,
            displayName: (d.get("displayName") as string) || "",
            email: (d.get("email") as string) || "",
            role: d.get("role") as Role,
            disabled: d.get("disabled") === true,
            offShiftRadiusKm: (d.get("offShiftRadiusKm") as number | null) ?? null,
            profile: (d.get("profile") as MemberRow["profile"]) ?? null,
            title: (d.get("title") as string | undefined) ?? "",
            phone: (d.get("phone") as string | undefined) ?? "",
          })),
        ),
      ),
      onSnapshot(collection(db, paths.tenantDepartments(tenantId)), (snap) =>
        setDepartments(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as Department) }))
            .map((d) => ({ ...d, managerUids: d.managerUids ?? [], memberUids: d.memberUids ?? [] }))
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
        ),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  const [importing, setImporting] = useState(false);
  async function refreshAdminData() {
    if (!tenantId || !isAdmin) return;
    try {
      const [inv, team] = await Promise.all([
        call<{ invites: InviteRow[] }>("listTenantInvites", { tenantId }, { mutation: false }),
        call<{ settings: Record<string, Sensitive> }>("getTeamSettings", { tenantId }, { mutation: false }),
      ]);
      setInvites(inv.invites);
      setSensitive(team.settings);
    } catch (err) {
      setMessage((err as Error).message);
    }
  }
  useEffect(() => {
    void refreshAdminData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, isAdmin]);

  const go = (t: Tab) => {
    setTab(t);
    setMessage("");
    window.history.replaceState(null, "", `#${t}`);
  };
  const tabs: Array<{ id: Tab; label: string; count: number }> = [
    { id: "people", label: "People", count: members.filter((m) => !m.disabled).length },
    ...(isAdmin ? [{ id: "invites" as Tab, label: "Waiting to join", count: invites.length }] : []),
    ...(deptsOn ? [{ id: "departments" as Tab, label: "Departments", count: departments.length }] : []),
    // Pay data: admins and the owner only.
    ...(isAdminRole ? [{ id: "commissions" as Tab, label: "Commissions", count: Object.values(sales.commissionDefaults).filter((v) => v > 0).length }] : []),
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-bold">Team</h1>
        {isAdmin ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" className="px-3 py-1.5 text-sm" onClick={() => setImporting((v) => !v)}>
              <FileUp size={14} className="mr-1 inline" /> Import team
            </Button>
            <Button className="px-3 py-1.5 text-sm" onClick={() => go("invites")}>
              <UserPlus size={14} className="mr-1 inline" /> Invite people
            </Button>
          </div>
        ) : null}
      </div>
      {importing && tenantId ? (
        <TeamImport
          tenantId={tenantId}
          meUid={user?.uid ?? ""}
          departments={departments.map((d) => ({ id: d.id, name: d.name, kind: d.kind }))}
          onClose={() => setImporting(false)}
          onDone={(msg) => {
            setMessage(msg);
            void refreshAdminData();
          }}
        />
      ) : null}
      <div className="inline-flex rounded-lg border border-steel/30 bg-white p-0.5 text-sm" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={cn("rounded-md px-3 py-1.5", tab === t.id ? "bg-ink font-semibold text-white" : "text-steel hover:text-ink")}
            onClick={() => go(t.id)}
          >
            {t.label} <span className={cn("ml-0.5 text-xs", tab === t.id ? "text-white/70" : "text-steel/80")}>{t.count}</span>
          </button>
        ))}
      </div>
      {message ? <p className="rounded-lg bg-cream px-3 py-1.5 text-xs text-ink">{message}</p> : null}

      {tab === "people" && tenantId ? (
        <PeoplePanel
          tenantId={tenantId}
          meUid={user?.uid ?? ""}
          isAdmin={isAdmin}
          canEditDepartments={isAdminRole}
          members={members}
          departments={deptsOn ? departments : []}
          sensitive={sensitive}
          onSaved={() => void refreshAdminData()}
          say={setMessage}
        />
      ) : null}
      {tab === "invites" && isAdmin && tenantId ? (
        <InvitesPanel tenantId={tenantId} invites={invites} setInvites={setInvites} refresh={refreshAdminData} say={setMessage} />
      ) : null}
      {tab === "departments" && tenantId && deptsOn ? (
        <DepartmentsPanel tenantId={tenantId} departments={departments} members={members} canEdit={isAdminRole} />
      ) : null}
      {tab === "commissions" && tenantId && isAdminRole ? <CommissionsPanel tenantId={tenantId} settings={sales} say={setMessage} /> : null}
    </div>
  );
}

// ---------- People ----------

export function PeoplePanel({
  tenantId,
  meUid,
  isAdmin,
  canEditDepartments,
  members,
  departments,
  sensitive,
  onSaved,
  say,
}: {
  tenantId: string;
  meUid: string;
  isAdmin: boolean;
  canEditDepartments: boolean;
  members: MemberRow[];
  departments: Dept[];
  sensitive: Record<string, Sensitive>;
  onSaved: () => void;
  say: (m: string) => void;
}) {
  const [roleFilter, setRoleFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const [status, setStatus] = useState("active");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const deptsOf = useMemo(() => {
    const m = new Map<string, Array<{ id: string; name: string; manager: boolean }>>();
    for (const d of departments) {
      for (const uid of new Set([...d.memberUids, ...d.managerUids])) {
        const list = m.get(uid) ?? [];
        list.push({ id: d.id, name: d.name, manager: d.managerUids.includes(uid) });
        m.set(uid, list);
      }
    }
    return m;
  }, [departments]);
  const rate = (uid: string) => sensitive[uid]?.hourlyRateCents ?? null;

  const view = useListView(members, {
    text: (m) => [m.displayName, m.email, m.title, m.phone, ROLE_LABEL[m.role], ...(deptsOf.get(m.uid) ?? []).map((d) => d.name)],
    sorts: {
      name: (a, b) => nameOf(a).localeCompare(nameOf(b), undefined, { numeric: true }),
      role: (a, b) => (ROLE_RANK[b.role] ?? 0) - (ROLE_RANK[a.role] ?? 0) || nameOf(a).localeCompare(nameOf(b), undefined, { numeric: true }),
      dept: (a, b) => ((deptsOf.get(a.uid) ?? [])[0]?.name ?? "~").localeCompare((deptsOf.get(b.uid) ?? [])[0]?.name ?? "~"),
      rate: (a, b) => (rate(a.uid) ?? -1) - (rate(b.uid) ?? -1),
    },
    defaultSort: "name",
    filter: (m) =>
      (roleFilter === "all" || m.role === roleFilter) &&
      (status === "all" || (status === "active" ? !m.disabled : m.disabled)) &&
      (deptFilter === "all" || (deptFilter === "none" ? !(deptsOf.get(m.uid)?.length) : (deptsOf.get(m.uid) ?? []).some((d) => d.id === deptFilter))),
  });
  const allPicked = view.filtered.length > 0 && view.filtered.every((m) => picked.has(m.uid));

  async function bulk(label: string, fn: (uid: string) => Promise<unknown>) {
    const uids = [...picked];
    setBusy(true);
    say("");
    const results = await Promise.allSettled(uids.map(fn));
    const failed = results.filter((r) => r.status === "rejected") as PromiseRejectedResult[];
    say(`${label}: ${uids.length - failed.length} done${failed.length ? `, ${failed.length} not (${(failed[0]!.reason as Error).message})` : ""}.`);
    setBusy(false);
    setPicked(new Set());
  }

  const cols = isAdmin
    ? "sm:grid-cols-[24px_minmax(0,2.4fr)_minmax(0,1fr)_minmax(0,1.5fr)_72px_72px_16px]"
    : "sm:grid-cols-[minmax(0,2.4fr)_minmax(0,1fr)_minmax(0,1.5fr)_72px_16px]";

  return (
    <Card className="p-0">
      <div className="flex flex-wrap items-center gap-2 border-b border-steel/10 p-3">
        <SearchBox className="min-w-48 flex-1" value={view.query} onChange={view.setQuery} placeholder="Search name, email, role, department…" />
        <Select className="w-auto py-1.5 text-sm" aria-label="Role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="all">All roles</option>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABEL[r]} ({members.filter((m) => m.role === r && !m.disabled).length})
            </option>
          ))}
        </Select>
        {departments.length ? (
          <Select className="w-auto py-1.5 text-sm" aria-label="Department" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
            <option value="all">All departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
            <option value="none">No department</option>
          </Select>
        ) : null}
        <Select className="w-auto py-1.5 text-sm" aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="active">Active</option>
          <option value="disabled">Disabled ({members.filter((m) => m.disabled).length})</option>
          <option value="all">Everyone</option>
        </Select>
      </div>

      {isAdmin ? (
        <div>
          <BulkBar count={picked.size} onClear={() => setPicked(new Set())}>
            <Select
              className="w-auto border-white/20 py-1 text-xs"
              aria-label="Set role for selected"
              value=""
              disabled={busy}
              onChange={(e) => {
                const r = e.target.value as Role;
                if (r) void bulk(`Role set to ${ROLE_LABEL[r]}`, (uid) => call("updateUserRole", { tenantId, uid, role: r }));
              }}
            >
              <option value="">Set role…</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </Select>
            {canEditDepartments && departments.length ? (
              <Select
                className="w-auto border-white/20 py-1 text-xs"
                aria-label="Add selected to a department"
                value=""
                disabled={busy}
                onChange={(e) => {
                  const d = departments.find((x) => x.id === e.target.value);
                  if (!d) return;
                  const uids = [...picked];
                  void updateDoc(doc(db, paths.tenantDepartment(tenantId, d.id)), { memberUids: arrayUnion(...uids), updatedAt: Date.now() }).then(() => {
                    say(`Added ${uids.length} to ${d.name}.`);
                    setPicked(new Set());
                  });
                }}
              >
                <option value="">Add to department…</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Select>
            ) : null}
          </BulkBar>
        </div>
      ) : null}

      <div className={cn("hidden items-center gap-3 border-b border-steel/10 px-4 py-2 sm:grid", cols)}>
        {isAdmin ? (
          <input
            type="checkbox"
            aria-label="Select all"
            checked={allPicked}
            onChange={() => setPicked(allPicked ? new Set() : new Set(view.filtered.map((m) => m.uid)))}
          />
        ) : null}
        <SortHeader label="Name" k="name" view={view} />
        <SortHeader label="Role" k="role" view={view} />
        <SortHeader label="Departments" k="dept" view={view} />
        {isAdmin ? <SortHeader label="Rate" k="rate" view={view} /> : null}
        <span className="text-[11px] font-semibold uppercase tracking-wide text-steel">Status</span>
        <span />
      </div>

      {view.page.map((m) => {
        const isSelf = m.uid === meUid;
        const depts = deptsOf.get(m.uid) ?? [];
        const r = rate(m.uid);
        const expanded = open === m.uid;
        const canOpen = isAdmin || isSelf || canEditDepartments;
        return (
          <div key={m.uid} className={cn("border-b border-steel/10 last:border-0", expanded && "bg-cream/40")}>
            <div
              className={cn("grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 px-4 py-1.5 sm:gap-3", cols, canOpen && "cursor-pointer hover:bg-cream/60")}
              onClick={() => canOpen && setOpen(expanded ? null : m.uid)}
            >
              {isAdmin ? (
                <input
                  type="checkbox"
                  aria-label={`Select ${nameOf(m)}`}
                  checked={picked.has(m.uid)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() =>
                    setPicked((s) => {
                      const n = new Set(s);
                      if (n.has(m.uid)) n.delete(m.uid);
                      else n.add(m.uid);
                      return n;
                    })
                  }
                />
              ) : null}
              <div className={cn("flex min-w-0 items-center gap-2", !isAdmin && "col-span-2 sm:col-span-1")}>
                <Avatar name={nameOf(m)} photoUrl={m.profile?.approved ? m.profile.photoUrl : null} size={26} />
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-ink">
                    {nameOf(m)}
                    {isSelf ? <span className="ml-1 text-xs font-normal text-steel">(you)</span> : null}
                  </div>
                  {m.displayName || m.title ? <div className="truncate text-[11px] text-steel">{[m.title, m.displayName ? m.email : "", m.phone].filter(Boolean).join(" · ")}</div> : null}
                </div>
              </div>
              <span className="hidden text-sm text-ink sm:block">{ROLE_LABEL[m.role] ?? m.role}</span>
              <span className={cn("flex min-w-0 flex-wrap items-center gap-1 pl-[34px] sm:col-span-1 sm:col-start-auto sm:pl-0", isAdmin ? "col-span-2 col-start-2" : "col-span-full")}>
                <span className="text-[11px] text-steel sm:hidden">{ROLE_LABEL[m.role] ?? m.role}</span>
                {depts.map((d) => (
                  <span key={d.id} className="truncate rounded-full bg-ink/10 px-2 py-0.5 text-[11px] text-ink">
                    {d.name}
                    {d.manager ? " · mgr" : ""}
                  </span>
                ))}
              </span>
              {isAdmin ? <span className="hidden text-sm text-ink sm:block">{r !== null ? `$${(r / 100).toFixed(2)}` : <span className="text-steel">—</span>}</span> : null}
              <span className={cn("hidden text-xs sm:block", m.disabled ? "text-danger" : "text-success")}>{m.disabled ? "Disabled" : "Active"}</span>
              <span className="row-start-1 text-steel sm:row-auto" style={{ gridColumnEnd: -1 }}>
                {canOpen ? expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} /> : null}
              </span>
            </div>
            {expanded ? (
              <PersonDetails
                tenantId={tenantId}
                m={m}
                isSelf={isSelf}
                isAdmin={isAdmin}
                canEditDepartments={canEditDepartments}
                departments={departments}
                rateCents={r}
                commissionBps={sensitive[m.uid]?.commissionRateBps ?? null}
                homeAddress={sensitive[m.uid]?.homeAddress ?? null}
                onSaved={onSaved}
                say={say}
              />
            ) : null}
          </div>
        );
      })}
      {view.filtered.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-steel">{members.length === 0 ? "Just you so far — invite your crew." : "Nobody matches."}</div>
      ) : null}
      <ListFooter shown={view.page.length} matching={view.filtered.length} total={view.total} onMore={view.more} onAll={view.all} noun="people" />
    </Card>
  );
}

function PersonDetails({
  tenantId,
  m,
  isSelf,
  isAdmin,
  canEditDepartments,
  departments,
  rateCents,
  commissionBps = null,
  homeAddress = null,
  onSaved,
  say,
}: {
  tenantId: string;
  m: MemberRow;
  isSelf: boolean;
  isAdmin: boolean;
  canEditDepartments: boolean;
  departments: Dept[];
  rateCents: number | null;
  /** Commission on what they sell, basis points (admins only). */
  commissionBps?: number | null;
  /** Home address (admins only, like pay). */
  homeAddress?: HomeAddress | null;
  onSaved: () => void;
  say: (s: string) => void;
}) {
  const [rateDraft, setRateDraft] = useState(rateCents !== null ? (rateCents / 100).toFixed(2) : "");
  const [commissionDraft, setCommissionDraft] = useState(commissionBps !== null ? String(commissionBps / 100) : "");
  // Blank = the default for their employee type (Team → Commissions).
  const defaultBps = useSalesSettings(tenantId).commissionDefaults[m.role] ?? null;
  const [radius, setRadius] = useState(m.offShiftRadiusKm != null ? String(Math.round(m.offShiftRadiusKm * 0.621371)) : "");
  const run = async (fn: () => Promise<unknown>, done?: string) => {
    say("");
    try {
      await fn();
      if (done) say(done);
    } catch (err) {
      say((err as Error).message);
    }
  };

  return (
    <div className="grid gap-3 px-4 pb-3 pt-1 text-sm sm:grid-cols-2 sm:pl-[3.25rem]">
      {isAdmin ? <TwoStepTeamLine tenantId={tenantId} uid={m.uid} name={nameOf(m)} role={m.role} isSelf={isSelf} /> : null}
      {isAdmin ? (
        <div>
          <Label>Role</Label>
          <Select value={m.role} onChange={(e) => void run(() => call("updateUserRole", { tenantId, uid: m.uid, role: e.target.value }), `${nameOf(m)} is now ${ROLE_LABEL[e.target.value as Role]}.`)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </Select>
        </div>
      ) : null}
      {departments.length ? (
        <div>
          <Label>Departments</Label>
          <div className="flex flex-wrap gap-1.5">
            {departments.map((d) => {
              const on = d.memberUids.includes(m.uid) || d.managerUids.includes(m.uid);
              return (
                <button
                  key={d.id}
                  type="button"
                  disabled={!canEditDepartments || d.managerUids.includes(m.uid)}
                  title={d.managerUids.includes(m.uid) ? "Manager of this department (change it on Departments)" : undefined}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-xs transition-colors",
                    on ? "border-ink bg-ink text-white" : "border-steel/30 bg-white text-ink hover:border-ink",
                    !canEditDepartments && "cursor-default",
                  )}
                  onClick={() =>
                    void run(() =>
                      updateDoc(doc(db, paths.tenantDepartment(tenantId, d.id)), {
                        memberUids: on ? arrayRemove(m.uid) : arrayUnion(m.uid),
                        updatedAt: Date.now(),
                      }),
                    )
                  }
                >
                  {on ? "✓ " : "+ "}
                  {d.name}
                  {d.managerUids.includes(m.uid) ? " (manager)" : ""}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {isAdmin ? (
        <div>
          <Label>Hourly rate ($/hr)</Label>
          <Input
            type="number"
            step="0.25"
            min="0"
            className="w-32"
            value={rateDraft}
            onChange={(e) => setRateDraft(e.target.value)}
            onBlur={() =>
              void run(async () => {
                const next = rateDraft === "" ? null : Math.round(parseFloat(rateDraft) * 100);
                if (next === rateCents) return;
                await call("updateMemberSettings", { tenantId, uid: m.uid, patch: { hourlyRateCents: next } });
                onSaved();
                say("Rate saved.");
              })
            }
          />
        </div>
      ) : null}
      {isAdmin ? (
        <div>
          <Label>Commission (% of what they sell; blank = the {ROLE_LABEL[m.role]} default, 0 = none)</Label>
          <Input
            type="number"
            step="0.25"
            min="0"
            max="100"
            className="w-32"
            placeholder={defaultBps != null ? `${defaultBps / 100}% (default)` : "None"}
            value={commissionDraft}
            onChange={(e) => setCommissionDraft(e.target.value)}
            onBlur={() =>
              void run(async () => {
                const next = commissionDraft === "" ? null : Math.max(0, Math.min(10000, Math.round(parseFloat(commissionDraft) * 100)));
                if (next === commissionBps) return;
                await call("updateMemberSettings", { tenantId, uid: m.uid, patch: { commissionRateBps: next } });
                onSaved();
                say("Commission saved. Team → Commissions sets how it's figured.");
              })
            }
          />
        </div>
      ) : null}
      {isAdmin || isSelf ? (
        <div>
          <Label>Off-shift drive radius (miles)</Label>
          <Input
            type="number"
            min="1"
            max="300"
            className="w-32"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
            onBlur={() =>
              void run(() =>
                // The UI speaks miles; the stored field stays km for the geo math.
                call("updateMemberSettings", {
                  tenantId,
                  uid: m.uid,
                  patch: { offShiftRadiusKm: radius === "" ? null : Math.max(1, Math.round(parseInt(radius, 10) * 1.60934)) },
                }),
              )
            }
          />
        </div>
      ) : null}
      {isAdmin ? (
        <div className="sm:col-span-2">
          <Label>Home address (admins only; comes in with a team import)</Label>
          <HomeAddressFields
            value={homeAddress}
            onSave={(next) =>
              void run(async () => {
                await call("updateMemberSettings", { tenantId, uid: m.uid, patch: { homeAddress: next } });
                onSaved();
                say(next ? "Address saved." : "Address cleared.");
              })
            }
          />
        </div>
      ) : null}
      {isAdmin && m.profile && (m.profile.bio || m.profile.photoUrl) ? (
        <div className="sm:col-span-2">
          <Label>Customer-facing profile</Label>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <Avatar name={nameOf(m)} photoUrl={m.profile.photoUrl} size={36} />
            <span className="min-w-0 flex-1 text-ink">{m.profile.bio || <span className="text-steel">No bio</span>}</span>
            <span className={m.profile.approved ? "text-success" : "text-brandorange-dark"}>{m.profile.approved ? "Approved" : "Waiting for approval"}</span>
            <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => void run(() => call("setMemberProfile", { tenantId, uid: m.uid, approved: !m.profile?.approved }))}>
              {m.profile.approved ? "Unapprove" : "Approve"}
            </Button>
            <label className="flex items-center gap-1 text-steel">
              <input
                type="checkbox"
                checked={m.profile.backgroundChecked === true}
                onChange={(e) => void run(() => call("setMemberProfile", { tenantId, uid: m.uid, backgroundChecked: e.target.checked }))}
              />
              Background checked
            </label>
          </div>
        </div>
      ) : null}
      {isAdmin && !isSelf ? (
        <div className="sm:col-span-2">
          {m.disabled ? (
            <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => void run(() => call("setUserStatus", { tenantId, uid: m.uid, disabled: false }), `${nameOf(m)} can sign in again.`)}>
              Enable {nameOf(m)}
            </Button>
          ) : (
            <ConfirmButton
              className="px-2 py-1 text-xs"
              confirm={`Disable ${nameOf(m)}? They can't sign in until enabled.`}
              confirmLabel="Disable"
              onConfirm={() => void run(() => call("setUserStatus", { tenantId, uid: m.uid, disabled: true }), `${nameOf(m)} is disabled.`)}
            >
              Disable…
            </ConfirmButton>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ---------- Waiting to join (invites) ----------

export function InvitesPanel({
  tenantId,
  invites,
  setInvites,
  refresh,
  say,
  copyToken = false,
}: {
  tenantId: string;
  invites: InviteRow[];
  setInvites: (fn: (prev: InviteRow[]) => InviteRow[]) => void;
  refresh: () => Promise<void>;
  say: (s: string) => void;
  /** Clevis staff: copy an invite's token to hand over by hand. */
  copyToken?: boolean;
}) {
  const [form, setForm] = useState({ emails: "", role: "staff" as Role });
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [show, setShow] = useState("all");

  const view = useListView(invites, {
    text: (i) => [i.email, ROLE_LABEL[i.role], i.lastEmailedAt ? "emailed" : "not emailed"],
    sorts: {
      email: (a, b) => a.email.localeCompare(b.email, undefined, { numeric: true }),
      role: (a, b) => (ROLE_RANK[b.role] ?? 0) - (ROLE_RANK[a.role] ?? 0) || a.email.localeCompare(b.email, undefined, { numeric: true }),
      emailed: (a, b) => (a.lastEmailedAt ?? 0) - (b.lastEmailedAt ?? 0),
      expires: (a, b) => a.expiresAt - b.expiresAt,
    },
    defaultSort: "email",
    filter: (i) => show === "all" || (show === "unsent" ? !i.lastEmailedAt : !!i.lastEmailedAt),
  });
  const allPicked = view.filtered.length > 0 && view.filtered.every((i) => picked.has(i.token));
  const unsent = invites.filter((i) => !i.lastEmailedAt).length;

  async function invite(e: FormEvent) {
    e.preventDefault();
    const emails = [...new Set(form.emails.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter(Boolean))];
    if (!emails.length) return;
    setBusy(true);
    say("");
    const results = await Promise.allSettled(emails.map((email) => call<{ emailed?: boolean }>("inviteUserToTenant", { tenantId, email, role: form.role })));
    const failed = emails.filter((_, i) => results[i]!.status === "rejected");
    const notEmailed = emails.filter((_, i) => results[i]!.status === "fulfilled" && !(results[i] as PromiseFulfilledResult<{ emailed?: boolean }>).value.emailed);
    say(
      `Invited ${emails.length - failed.length} as ${ROLE_LABEL[form.role]}.` +
        (notEmailed.length ? ` The email didn't send to ${notEmailed.join(", ")}; use Re-send.` : "") +
        (failed.length ? ` Couldn't invite ${failed.join(", ")}: ${((results.find((r) => r.status === "rejected") as PromiseRejectedResult).reason as Error).message}` : ""),
    );
    setForm({ emails: "", role: form.role });
    setBusy(false);
    await refresh();
  }

  async function setRole(tokens: string[], role: Role) {
    setInvites((prev) => prev.map((x) => (tokens.includes(x.token) ? { ...x, role } : x)));
    const results = await Promise.allSettled(tokens.map((token) => call("setInviteRole", { tenantId, token, role })));
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed) {
      say(`${failed} role change${failed === 1 ? "" : "s"} didn't save.`);
      await refresh();
    } else if (tokens.length > 1) say(`${tokens.length} invites set to ${ROLE_LABEL[role]}.`);
  }

  async function sendPicked() {
    setSending(true);
    say("");
    try {
      const res = await call<{ sent: number; failed: string[] }>("sendTenantInvites", { tenantId, tokens: [...picked] });
      say(`Emailed ${res.sent} invite${res.sent === 1 ? "" : "s"}.` + (res.failed.length ? ` Didn't send to: ${res.failed.join(", ")}.` : ""));
      setPicked(new Set());
      await refresh();
    } catch (err) {
      say((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  const cols = copyToken ? "sm:grid-cols-[24px_minmax(0,2.2fr)_140px_minmax(0,1fr)_72px_210px]" : "sm:grid-cols-[24px_minmax(0,2.2fr)_140px_minmax(0,1fr)_72px_136px]";
  return (
    <div className="space-y-3" id="invites">
      <Card>
        <CardTitle>Invite people</CardTitle>
        <form onSubmit={invite} className="flex flex-wrap items-end gap-2">
          <div className="min-w-56 flex-1">
            <Label>Email addresses (one or many, separated by commas or spaces)</Label>
            <Input required value={form.emails} placeholder="pat@company.com, sam@company.com" onChange={(e) => setForm({ ...form, emails: e.target.value })} />
          </div>
          <div>
            <Label>Joins as</Label>
            <Select className="w-auto" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </Select>
          </div>
          <Button type="submit" busy={busy}>
            Send invite
          </Button>
        </form>
        <p className="mt-2 text-[11px] text-steel">Each person gets an email link. They can also just sign in with Google using the invited address.</p>
      </Card>

      <Card className="p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-steel/10 p-3">
          <SearchBox className="min-w-48 flex-1" value={view.query} onChange={view.setQuery} placeholder="Search invites…" />
          <Select className="w-auto py-1.5 text-sm" aria-label="Show" value={show} onChange={(e) => setShow(e.target.value)}>
            <option value="all">All ({invites.length})</option>
            <option value="unsent">Not emailed yet ({unsent})</option>
            <option value="sent">Emailed ({invites.length - unsent})</option>
          </Select>
          {unsent ? (
            <button className="text-xs text-steel underline hover:text-ink" onClick={() => setPicked(new Set(invites.filter((i) => !i.lastEmailedAt).map((i) => i.token)))}>
              Select the unsent
            </button>
          ) : null}
        </div>
        <div>
          <BulkBar count={picked.size} onClear={() => setPicked(new Set())}>
            <Select
              className="w-auto border-white/20 py-1 text-xs"
              aria-label="Set role for selected"
              value=""
              onChange={(e) => {
                if (e.target.value) void setRole([...picked], e.target.value as Role);
              }}
            >
              <option value="">Set role…</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </Select>
            <span className="[&>span>span:first-child]:text-white">
              <ConfirmButton
                variant="primary"
                className="px-2.5 py-1 text-xs"
                busy={sending}
                confirm={`Email ${picked.size} invite${picked.size === 1 ? "" : "s"} now?`}
                confirmLabel="Send"
                onConfirm={() => void sendPicked()}
              >
                Email {picked.size} invite{picked.size === 1 ? "" : "s"}
              </ConfirmButton>
            </span>
          </BulkBar>
        </div>
        <p className="px-4 pt-2 text-[11px] text-steel">Set each person's role before emailing: they join as that role.</p>
        <div className={cn("hidden items-center gap-3 border-b border-steel/10 px-4 py-2 sm:grid", cols)}>
          <input type="checkbox" aria-label="Select all" checked={allPicked} onChange={() => setPicked(allPicked ? new Set() : new Set(view.filtered.map((i) => i.token)))} />
          <SortHeader label="Email" k="email" view={view} />
          <SortHeader label="Joins as" k="role" view={view} />
          <SortHeader label="Emailed" k="emailed" view={view} />
          <SortHeader label="Expires" k="expires" view={view} />
          <span />
        </div>
        {view.page.map((i) => (
          <div key={i.token} className={cn("grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0 sm:gap-3", cols)}>
            <input
              type="checkbox"
              aria-label={`Select ${i.email}`}
              checked={picked.has(i.token)}
              onChange={() =>
                setPicked((s) => {
                  const n = new Set(s);
                  if (n.has(i.token)) n.delete(i.token);
                  else n.add(i.token);
                  return n;
                })
              }
            />
            <span className="flex min-w-0 items-center gap-2">
              <Avatar name={i.email} size={24} />
              <span className="truncate text-ink">{i.email}</span>
            </span>
            <span className="col-start-2 sm:col-start-auto">
              <Select className="w-full border-transparent bg-transparent px-2 py-1 text-xs hover:border-steel/40 hover:bg-white" aria-label={`Role for ${i.email}`} value={i.role} onChange={(e) => void setRole([i.token], e.target.value as Role)}>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </Select>
            </span>
            <span className={cn("col-start-2 text-xs sm:col-start-auto", i.lastEmailedAt ? "text-steel" : "text-brandorange-dark")}>
              {i.lastEmailedAt ? `Emailed ${shortDay(i.lastEmailedAt)}` : "Not emailed yet"}
            </span>
            <span className={cn("col-start-2 text-xs sm:col-start-auto", i.expiresAt < Date.now() ? "text-danger" : "text-steel")}>
              {i.expiresAt < Date.now() ? "Expired" : shortDay(i.expiresAt)}
            </span>
            <span className="col-start-2 flex items-center gap-3 whitespace-nowrap text-xs sm:col-start-auto sm:justify-end">
              <button
                className="text-steel hover:text-ink"
                onClick={async () => {
                  try {
                    await call("inviteUserToTenant", { tenantId, email: i.email, role: i.role });
                    say(`Invite re-sent to ${i.email}.`);
                    await refresh();
                  } catch (err) {
                    say((err as Error).message);
                  }
                }}
              >
                {i.lastEmailedAt ? "Re-send" : "Send"}
              </button>
              {copyToken ? (
                <button
                  className="text-steel hover:text-ink"
                  onClick={() => {
                    void navigator.clipboard.writeText(i.token);
                    say(`Token for ${i.email} copied.`);
                  }}
                >
                  Copy token
                </button>
              ) : null}
              <ConfirmButton
                className="px-1 py-0 text-xs text-danger/80"
                confirm="Revoke?"
                confirmLabel="Revoke"
                onConfirm={async () => {
                  await call("revokeInvite", { tenantId, token: i.token });
                  await refresh();
                }}
              >
                Revoke
              </ConfirmButton>
            </span>
          </div>
        ))}
        {view.filtered.length === 0 ? <div className="px-4 py-8 text-center text-sm text-steel">{invites.length ? "Nothing matches." : "Nobody is waiting to join."}</div> : null}
        <ListFooter shown={view.page.length} matching={view.filtered.length} total={view.total} onMore={view.more} onAll={view.all} noun="invites" />
      </Card>
    </div>
  );
}

// ---------- Departments ----------

// Departments: route incoming jobs and requests to a team, let its manager
// assign the techs, and filter lists by it. Skip them and Clevis behaves like
// a one-crew shop.
export function DepartmentsPanel({ tenantId, departments, members, canEdit }: { tenantId: string; departments: Dept[]; members: MemberRow[]; canEdit: boolean }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"department" | "group">("department");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const active = members.filter((m) => !m.disabled).sort((a, b) => nameOf(a).localeCompare(nameOf(b), undefined, { numeric: true }));
  const byUid = new Map(members.map((m) => [m.uid, m]));

  async function add(e: FormEvent) {
    e.preventDefault();
    const n = name.replace(/\s+/g, " ").trim();
    if (n.length < 2) return;
    if (departments.some((d) => d.name.toLowerCase() === n.toLowerCase())) return;
    setBusy(true);
    try {
      const dept: Department = { name: n, kind, managerUids: [], memberUids: [], createdAt: Date.now(), updatedAt: Date.now() };
      await addDoc(collection(db, paths.tenantDepartments(tenantId)), dept);
      setName("");
    } finally {
      setBusy(false);
    }
  }
  const update = (id: string, patch: Record<string, unknown>) => updateDoc(doc(db, paths.tenantDepartment(tenantId, id)), { ...patch, updatedAt: Date.now() });

  return (
    <Card className="p-0">
      <div className="space-y-2 border-b border-steel/10 p-3">
        <p className="text-xs text-steel">
          <strong className="text-ink">Departments</strong> get jobs and requests routed to them (Fireplace, Garage Service, Production…), let
          each manager assign their own techs, and filter Jobs, Schedule, Timesheets and the dashboard. <strong className="text-ink">Groups</strong>{" "}
          cut across departments (Operations, Sales, Management): they filter people, never get jobs. A person can be in several.
        </p>
        {canEdit ? (
          <form onSubmit={add} className="flex flex-wrap gap-2">
            <Input className="max-w-sm flex-1" placeholder={kind === "group" ? "New group, e.g. Operations" : "New department, e.g. Fireplace"} value={name} onChange={(e) => setName(e.target.value)} />
            <Select className="w-auto" aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value as "department" | "group")}>
              <option value="department">Department</option>
              <option value="group">Group (cross-department)</option>
            </Select>
            <Button type="submit" busy={busy} disabled={name.trim().length < 2}>
              Add {kind === "group" ? "group" : "department"}
            </Button>
          </form>
        ) : null}
      </div>
      {departments.map((d) => {
        const people = [...new Set([...d.managerUids, ...d.memberUids])].map((uid) => byUid.get(uid)).filter((m): m is MemberRow => !!m);
        const expanded = open === d.id;
        const manager = d.managerUids[0] ?? "";
        return (
          <div key={d.id} className="border-b border-steel/10 last:border-0">
            <div className="flex flex-wrap items-center gap-3 px-4 py-2">
              <button className="flex min-w-40 flex-1 items-center gap-2 text-left" onClick={() => setOpen(expanded ? null : d.id)}>
                {expanded ? <ChevronDown size={14} className="text-steel" /> : <ChevronRight size={14} className="text-steel" />}
                <span className="truncate text-sm font-semibold text-ink">{d.name}</span>
                {d.kind === "group" ? <span className="rounded-full bg-brandorange/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-brandorange-dark">group</span> : null}
                <span className="text-xs text-steel">
                  {people.length} {people.length === 1 ? "person" : "people"}
                </span>
              </button>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-steel">Manager</span>
                <Select
                  className="w-56 py-1 text-xs"
                  aria-label={`Manager of ${d.name}`}
                  disabled={!canEdit}
                  value={manager}
                  onChange={(e) => {
                    const uid = e.target.value;
                    void update(d.id, { managerUids: uid ? [uid] : [], ...(uid ? { memberUids: arrayUnion(uid) } : {}) });
                  }}
                >
                  <option value="">No manager</option>
                  {active.map((m) => (
                    <option key={m.uid} value={m.uid}>
                      {nameOf(m)}
                    </option>
                  ))}
                </Select>
                {canEdit ? (
                  <ConfirmButton
                    className="px-2 py-1 text-xs"
                    confirm={`Delete ${d.name}? Its jobs keep working, just without a department.`}
                    confirmLabel="Delete"
                    onConfirm={() => void deleteDoc(doc(db, paths.tenantDepartment(tenantId, d.id)))}
                  >
                    Delete
                  </ConfirmButton>
                ) : null}
              </div>
            </div>
            {expanded ? (
              <div className="space-y-2 bg-cream/40 px-4 pb-3 pt-1">
                <div className="flex flex-wrap gap-1.5">
                  {people.map((m) => (
                    <span key={m.uid} className="flex items-center gap-1.5 rounded-full border border-steel/25 bg-white py-0.5 pl-0.5 pr-2 text-xs text-ink">
                      <Avatar name={nameOf(m)} size={20} />
                      {nameOf(m)}
                      {d.managerUids.includes(m.uid) ? <span className="text-steel">· manager</span> : null}
                      {canEdit && !d.managerUids.includes(m.uid) ? (
                        <button aria-label={`Remove ${nameOf(m)}`} className="text-steel hover:text-danger" onClick={() => void update(d.id, { memberUids: arrayRemove(m.uid) })}>
                          ×
                        </button>
                      ) : null}
                    </span>
                  ))}
                  {people.length === 0 ? <span className="text-xs text-steel">Nobody in {d.name} yet.</span> : null}
                </div>
                {canEdit ? (
                  <Select
                    className="w-72 py-1 text-xs"
                    aria-label={`Add someone to ${d.name}`}
                    value=""
                    searchable
                    onChange={(e) => {
                      if (e.target.value) void update(d.id, { memberUids: arrayUnion(e.target.value) });
                    }}
                  >
                    <option value="">+ Add someone to {d.name}…</option>
                    {active
                      .filter((m) => !people.some((p) => p.uid === m.uid))
                      .map((m) => (
                        <option key={m.uid} value={m.uid}>
                          {nameOf(m)} ({ROLE_LABEL[m.role]})
                        </option>
                      ))}
                  </Select>
                ) : null}
                <p className="text-[11px] text-steel">Tip: on People, tick several and use "Add to department" to fill it in one go.</p>
              </div>
            ) : null}
          </div>
        );
      })}
      {departments.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-steel">No departments yet.{canEdit ? " Add your first one above." : ""}</div>
      ) : null}
    </Card>
  );
}

const BLANK_ADDRESS: HomeAddress = { line1: "", line2: "", city: "", state: "", zip: "" };

/** Street / apt / city / state / ZIP; saves when you leave a changed field, blank everywhere clears it. */
function HomeAddressFields({ value, onSave }: { value: HomeAddress | null; onSave: (next: HomeAddress | null) => void }) {
  const [d, setD] = useState<HomeAddress>({ ...BLANK_ADDRESS, ...(value ?? {}) });
  const commit = () => {
    const trimmed: HomeAddress = { line1: d.line1.trim(), line2: d.line2.trim(), city: d.city.trim(), state: d.state.trim().length === 2 ? d.state.trim().toUpperCase() : d.state.trim(), zip: d.zip.trim() };
    const next = Object.values(trimmed).some(Boolean) ? trimmed : null;
    if (formatHomeAddress(next) === formatHomeAddress(value)) return;
    onSave(next);
  };
  const field = (k: keyof HomeAddress, placeholder: string) => (
    <Input placeholder={placeholder} aria-label={placeholder} value={d[k]} onChange={(e) => setD({ ...d, [k]: e.target.value })} onBlur={commit} />
  );
  return (
    <div className="grid max-w-xl grid-cols-6 gap-2">
      <div className="col-span-6 sm:col-span-4">{field("line1", "Street")}</div>
      <div className="col-span-6 sm:col-span-2">{field("line2", "Apt / unit")}</div>
      <div className="col-span-3">{field("city", "City")}</div>
      <div className="col-span-1">{field("state", "ST")}</div>
      <div className="col-span-2">{field("zip", "ZIP")}</div>
    </div>
  );
}
